import React, { useState, useEffect, useMemo, useCallback } from 'react';

import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import SmartLoadingBuffer from '../components/ui/SmartLoadingBuffer';
import useAuth from '../hooks/useAuth';
import { createKnowledgeEntry, fetchKnowledgeEntries, updateKnowledgeFavorite } from '../services/knowledgeService';

const KnowledgePage = () => {
  const { user, initializing } = useAuth();
  const [knowledge, setKnowledge] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [viewMode, setViewMode] = useState('all');
  const [error, setError] = useState(null);
  const [favoriteSyncingId, setFavoriteSyncingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const difficultyOptions = useMemo(
    () => ['Quick skim', 'Deep dive', 'Workshop', 'Reflection', 'Case study'],
    []
  );

  const initialFormState = useMemo(
    () => ({
      title: '',
      summary: '',
      content: '',
      category: '',
      difficulty: 'Quick skim',
      readMinutes: '',
      tags: '',
      relatedHabits: '',
      source: ''
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormState);

  const viewModes = [
    { id: 'all', label: 'All items', icon: 'Layers' },
    { id: 'favorites', label: 'Favorites', icon: 'Star' },
    { id: 'recent', label: 'Recently updated', icon: 'Clock' }
  ];

  const loadKnowledge = useCallback(async () => {
    if (!user?.id) {
      setKnowledge([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const entries = await fetchKnowledgeEntries(user.id);
      setKnowledge(entries);
    } catch (err) {
      console.error('Error loading knowledge:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!initializing) {
      loadKnowledge();
    }
  }, [initializing, loadKnowledge]);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(knowledge?.map(item => item?.category).filter(Boolean)));
    return ['All', ...unique];
  }, [knowledge]);

  const difficulties = useMemo(() => {
    const unique = Array.from(new Set(knowledge?.map(item => item?.difficulty).filter(Boolean)));
    return ['All', ...unique];
  }, [knowledge]);

  const recentCutoff = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 14);
    return date;
  }, []);

  const filteredKnowledge = knowledge?.filter(item => {
    const matchesSearch = item?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         item?.summary?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         item?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || item?.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'All' || item?.difficulty === selectedDifficulty;
    const matchesViewMode = viewMode === 'all'
      ? true
      : viewMode === 'favorites'
        ? item?.favorite
        : new Date(item?.lastModified) >= recentCutoff;

    return matchesSearch && matchesCategory && matchesDifficulty && matchesViewMode;
  });

  const toggleFavorite = useCallback(async (id) => {
    if (!user?.id) {
      return;
    }

    let nextFavorite = false;

    setKnowledge(prevKnowledge =>
      prevKnowledge?.map(item => {
        if (item?.id === id) {
          nextFavorite = !item?.favorite;
          return { ...item, favorite: nextFavorite };
        }
        return item;
      })
    );

    setFavoriteSyncingId(id);
    setError(null);

    try {
      await updateKnowledgeFavorite({ entryId: id, userId: user.id, favorite: nextFavorite });
    } catch (err) {
      console.error('Failed to update favorite state:', err);
      setError(err);
      setKnowledge(prevKnowledge =>
        prevKnowledge?.map(item =>
          item?.id === id ? { ...item, favorite: !nextFavorite } : item
        )
      );
    } finally {
      setFavoriteSyncingId(null);
    }
  }, [user?.id]);

  const totalReadMinutes = useMemo(() => {
    return knowledge?.reduce((total, item) => total + (item?.readMinutes || 0), 0);
  }, [knowledge]);

  const favoriteCount = useMemo(() => {
    return knowledge?.filter(item => item?.favorite)?.length;
  }, [knowledge]);

  const curatedCollections = useMemo(() => ([
    {
      id: 'habit-boosters',
      title: 'Weekly habit boosters',
      description: 'Refresh your routines with quick reads tied to your active habits.',
      items: knowledge?.filter(item => item?.relatedHabits?.length)?.slice(0, 3)
    },
    {
      id: 'deep-work-friday',
      title: 'Deep work Friday stack',
      description: 'Choose one deep dive each week to expand long-term understanding.',
      items: knowledge?.filter(item => item?.difficulty === 'Deep dive')?.slice(0, 3)
    }
  ]), [knowledge]);

  const suggestions = useMemo(() => ([
    {
      icon: 'Brain',
      title: 'Send to flashcards',
      description: 'Capture difficult points as flashcards so review sessions surface them automatically.',
      action: () => window.location.href = '/flashcards'
    },
    {
      icon: 'Target',
      title: 'Link to a habit',
      description: 'Transform this insight into a weekly or daily habit to reinforce the learning loop.',
      action: () => window.location.href = '/habits'
    },
    {
      icon: 'Sparkles',
      title: 'Ask AI for a summary',
      description: 'Send a note to the assistant to condense key takeaways into a two-minute refresher.',
      action: () => window.location.href = '/assistant'
    }
  ]), []);

  const formatReadTime = (minutes) => `${minutes} min read`;

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
    setFormSuccess(null);
  }, []);

  const handleCreateEntry = useCallback(async (event) => {
    event.preventDefault();

    if (!user?.id) {
      setFormError('You need to be signed in to add a note.');
      return;
    }

    if (!formData.title?.trim()) {
      setFormError('Add a descriptive title so you can find this note later.');
      return;
    }

    const parsedMinutes = formData.readMinutes?.trim() ? Number(formData.readMinutes) : null;
    if (formData.readMinutes?.trim() && (Number.isNaN(parsedMinutes) || parsedMinutes < 0)) {
      setFormError('Read time should be a positive number of minutes.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      await createKnowledgeEntry({
        userId: user.id,
        title: formData.title,
        summary: formData.summary,
        content: formData.content,
        category: formData.category,
        tags: formData.tags,
        difficulty: formData.difficulty,
        readMinutes: parsedMinutes,
        source: formData.source,
        relatedHabits: formData.relatedHabits,
        type: 'note'
      });

      setFormSuccess('Note added successfully. Keep capturing insights to power your learning loop.');
      setFormData(initialFormState);
      setShowCreateForm(false);
      await loadKnowledge();
    } catch (err) {
      console.error('Failed to create knowledge entry:', err);
      setFormError(err?.message || 'We could not save your note. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, initialFormState, loadKnowledge, user?.id]);

  if (isLoading) {
    return (
      <AuthenticationGuard isAuthenticated={true}>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Mapping your knowledge base"
              message="We're syncing entries, tags, and habits so your insights stay connected."
              hint="Hang tight—Cogito is organizing what matters most."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard isAuthenticated={true}>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Knowledge Base</h1>
                  <p className="text-muted-foreground">Curate insights, sync with habits, and revisit notes when you need them most.</p>
                </div>
                <Button
                  className="hidden sm:flex"
                  onClick={() => {
                    setShowCreateForm(true);
                    setFormError(null);
                    setFormSuccess(null);
                  }}
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Add Note
                </Button>
              </div>
            </div>

            {formSuccess && (
              <div className="mb-6 rounded-lg border border-success/20 bg-success/10 p-4 text-sm text-success">
                {formSuccess}
              </div>
            )}

            {/* Search and Filter */}
            <div className="mb-8 space-y-4">
              <div className="relative">
                <Icon 
                  name="Search" 
                  size={20} 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" 
                />
                <Input
                  type="text"
                  placeholder="Search notes, tags, or content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {categories?.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedCategory === category
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap gap-2">
                {difficulties?.map((difficulty) => (
                  <button
                    key={difficulty}
                    onClick={() => setSelectedDifficulty(difficulty)}
                    className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      selectedDifficulty === difficulty
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {difficulty}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="BookOpen" size={24} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{knowledge?.length}</p>
                    <p className="text-sm text-muted-foreground">Total Notes</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="Star" size={24} className="text-warning" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{favoriteCount}</p>
                    <p className="text-sm text-muted-foreground">Favorites</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="FolderOpen" size={24} className="text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {Math.max(categories?.length - 1, 0)}
                    </p>
                    <p className="text-sm text-muted-foreground">Categories</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Clock" size={24} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{totalReadMinutes}</p>
                    <p className="text-sm text-muted-foreground">Min to Read</p>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 rounded-lg border border-error/20 bg-error/10 p-4 text-sm text-error">
                {error?.message || 'Something went wrong while loading your knowledge. Please try again.'}
              </div>
            )}

            {showCreateForm && (
              <div className="mb-10 bg-card border border-border rounded-xl p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="PenSquare" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-foreground">Capture a new insight</h2>
                    <p className="text-sm text-muted-foreground">Add context so future study sessions can resurface this knowledge at the right time.</p>
                  </div>
                </div>

                {formError && (
                  <div className="mb-4 rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error">
                    {formError}
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleCreateEntry}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Title</span>
                      <Input
                        name="title"
                        value={formData.title}
                        onChange={handleFormChange}
                        placeholder="e.g., Active recall vs passive review"
                        required
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Category</span>
                      <Input
                        name="category"
                        value={formData.category}
                        onChange={handleFormChange}
                        placeholder="Programming, Language, Wellness..."
                      />
                    </label>
                  </div>

                  <label className="flex flex-col space-y-2">
                    <span className="text-sm font-medium text-foreground">Summary</span>
                    <textarea
                      name="summary"
                      value={formData.summary}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[80px]"
                      placeholder="Capture the key insight in two sentences."
                    />
                  </label>

                  <label className="flex flex-col space-y-2">
                    <span className="text-sm font-medium text-foreground">Detailed notes (optional)</span>
                    <textarea
                      name="content"
                      value={formData.content}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[140px]"
                      placeholder="Add examples, references, or links for deeper dives."
                    />
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Difficulty</span>
                      <select
                        name="difficulty"
                        value={formData.difficulty}
                        onChange={handleFormChange}
                        className="rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                      >
                        {difficultyOptions.map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </label>

                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Read time (minutes)</span>
                      <Input
                        name="readMinutes"
                        type="number"
                        min="0"
                        value={formData.readMinutes}
                        onChange={handleFormChange}
                        placeholder="5"
                      />
                    </label>

                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Source</span>
                      <Input
                        name="source"
                        value={formData.source}
                        onChange={handleFormChange}
                        placeholder="Article, course, conversation..."
                      />
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Tags</span>
                      <Input
                        name="tags"
                        value={formData.tags}
                        onChange={handleFormChange}
                        placeholder="Comma separated (e.g., memory, spaced repetition)"
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Related habits</span>
                      <Input
                        name="relatedHabits"
                        value={formData.relatedHabits}
                        onChange={handleFormChange}
                        placeholder="Comma separated (e.g., morning review)"
                      />
                    </label>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                    <Button type="submit" disabled={isSubmitting} iconName="Save">
                      {isSubmitting ? 'Saving...' : 'Save note'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      iconName="X"
                      onClick={() => {
                        setShowCreateForm(false);
                        setFormData(initialFormState);
                        setFormError(null);
                        setFormSuccess(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* View Modes */}
            <div className="mb-10">
              <div className="flex flex-wrap gap-2">
                {viewModes?.map((mode) => (
                  <button
                    key={mode?.id}
                    onClick={() => setViewMode(mode?.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg border transition-colors ${
                      viewMode === mode?.id
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-muted/40'
                    }`}
                  >
                    <Icon name={mode?.icon} size={14} />
                    <span className="text-sm font-medium">{mode?.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Curated Collections */}
            <div className="mb-12 space-y-6">
              {curatedCollections?.map((collection) => (
                <div key={collection?.id} className="bg-muted/20 border border-border rounded-xl p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 gap-3">
                    <div>
                      <h2 className="text-lg font-semibold text-foreground">{collection?.title}</h2>
                      <p className="text-sm text-muted-foreground">{collection?.description}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="ArrowRight"
                      onClick={() => window.location.href = '/flashcards'}
                    >
                      Explore related material
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {collection?.items?.length ? collection?.items?.map((item) => (
                      <div key={`${collection?.id}-${item?.id}`} className="bg-card border border-border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-foreground leading-tight line-clamp-2">{item?.title}</h3>
                          <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">{item?.difficulty}</span>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-3">{item?.summary}</p>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>{item?.category}</span>
                          <span>{formatReadTime(item?.readMinutes)}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => window.location.href = `/knowledge/${item?.id}`}
                        >
                          Open note
                        </Button>
                      </div>
                    )) : (
                      <div className="sm:col-span-3 text-sm text-muted-foreground">Add notes to see personalised recommendations.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Knowledge Items */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredKnowledge?.map((item) => (
                <div
                  key={item?.id}
                  className="bg-card rounded-xl border border-border p-6 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={24} className="text-primary" />
                    </div>
                    <button
                      onClick={() => toggleFavorite(item?.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        item?.favorite 
                          ? 'text-warning bg-warning/10 hover:bg-warning/20'
                          : 'text-muted-foreground hover:bg-muted'
                      } ${favoriteSyncingId === item?.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                      disabled={favoriteSyncingId === item?.id}
                    >
                      <Icon name={item?.favorite ? "Star" : "Star"} size={16} />
                    </button>
                  </div>

                  <div className="mb-4">
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      {item?.title}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {item?.summary}
                    </p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-1">
                      {item?.tags?.slice(0, 3)?.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs bg-muted px-2 py-1 rounded-full text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                      {item?.tags?.length > 3 && (
                        <span className="text-xs text-muted-foreground">
                          +{item?.tags?.length - 3} more
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>{item?.category}</span>
                      <span>{formatReadTime(item?.readMinutes)}</span>
                    </div>

                    <div className="text-xs text-muted-foreground">
                      Modified: {new Date(item?.lastModified)?.toLocaleDateString()}
                    </div>

                    {item?.relatedHabits?.length > 0 && (
                      <div className="bg-primary/10 text-xs text-primary rounded-md px-2 py-2">
                        Reinforce with habit: {item?.relatedHabits?.join(', ')}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 pt-4 border-t border-border space-y-2">
                    <Button variant="outline" size="sm" className="w-full">
                      <Icon name="Eye" size={14} className="mr-2" />
                      Read More
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Link"
                      className="w-full justify-start"
                      onClick={() => window.location.href = '/habits'}
                    >
                      Tie to habit plan
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {filteredKnowledge?.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Icon name="Search" size={32} className="text-muted-foreground" />
                </div>
                <p className="text-lg font-medium text-foreground mb-2">No notes found</p>
                <p className="text-muted-foreground">
                  Try adjusting your search or create a new note
                </p>
              </div>
            )}

            {/* Suggestions */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
              {suggestions?.map((suggestion, index) => (
                <div key={index} className="bg-card border border-border rounded-xl p-5 space-y-3">
                  <div className="w-10 h-10 bg-muted/40 rounded-lg flex items-center justify-center">
                    <Icon name={suggestion?.icon} size={18} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">{suggestion?.title}</h3>
                    <p className="text-sm text-muted-foreground">{suggestion?.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="justify-start px-0"
                    iconName="ArrowUpRight"
                    onClick={suggestion?.action}
                  >
                    Go
                  </Button>
                </div>
              ))}
            </div>

            {/* Mobile Add Button */}
            <div className="sm:hidden fixed bottom-6 right-6">
              <Button
                size="lg"
                className="w-14 h-14 rounded-full shadow-lg"
                onClick={() => {
                  setShowCreateForm(true);
                  setFormError(null);
                  setFormSuccess(null);
                }}
              >
                <Icon name="Plus" size={24} />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default KnowledgePage;